// src/utils/paginationUtils.js
//
// Pure utility for generating condensed pagination page-number sequences.
//
// The pagination bar needs to display a compact set of page numbers that always
// includes the first page, last page, current page, and up to two "neighbour"
// pages on either side of the current page. Gaps between non-adjacent visible
// pages are represented by ellipsis markers.
//
// This function is intentionally kept as a pure, zero-dependency utility so it
// can be exhaustively unit-tested without any rendering or mocking overhead.
// The presentational CondensedPagination component consumes its output.

/**
 * Generates the condensed page-number sequence for a pagination bar.
 *
 * When there are 7 or fewer total pages, every page number is returned (no
 * ellipsis) because the full sequence already fits comfortably. Above 7 pages,
 * the function returns: first page, (optional ellipsis), up to 2 neighbours
 * before current, current page, up to 2 neighbours after current, (optional
 * ellipsis), last page.
 *
 * The threshold of 7 avoids a jarring visual transition at small page counts —
 * inserting "..." into a 6-page list would save no meaningful space and look
 * odd. It also means the condensed logic only activates once the list is large
 * enough for it to provide real value.
 *
 * @param {number} currentPage  1-indexed active page.
 * @param {number} totalPages   Total number of pages (must be >= 1).
 * @returns {Array<number|'ellipsis'>} Ordered sequence of page numbers and
 *   'ellipsis' string markers. Returns an empty array for invalid inputs.
 */
export function getPageNumbers(currentPage, totalPages) {
  // Defensive: invalid or degenerate inputs
  if (!totalPages || totalPages < 1 || !currentPage || currentPage < 1) {
    return [];
  }

  // Clamp currentPage if it exceeds totalPages (caller may have a stale value
  // during a transition — this avoids producing an invalid sequence).
  const clamped = Math.min(currentPage, totalPages);

  // For small page counts, show every page — ellipsis would save no space.
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // Build the set of pages that should be visible
  const pages = new Set();
  pages.add(1);           // Always show first page
  pages.add(totalPages);  // Always show last page
  pages.add(clamped);     // Always show current page

  // Add up to 2 neighbours on each side of the current page
  for (let i = clamped - 2; i <= clamped + 2; i++) {
    if (i > 1 && i < totalPages) {
      pages.add(i);
    }
  }

  // Sort numerically and insert ellipsis markers where there are gaps
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];

  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push('ellipsis');
    }
    result.push(sorted[i]);
  }

  return result;
}
